import {
    Component,
    forwardRef,
    inject,
    OnInit,
    QueryList,
    ViewChildren,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { HandelssortimentsgruppDTO } from "~app/common/models/common/HandelssortimentsgruppDTO";
import { RestriktionsTyper } from "~app/common/models/constants";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";

import { IHandelssortimentFilter } from "~app/features/planera/model/IHandelssortimentFilter";
import { IMaskinlag } from "~app/features/planera/model/IMaskinLag";
import { ArbetsorderSearchService } from "~app/features/planera/services/arbetsorder-search.service";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ArbetsorderSearchFilterComponent } from "../../components/arbetsorder-search-filter/arbetsorder-search-filter.component";
import { ArbetsorderSearchBase } from "../../models/arbetsorder-search-base";
import { ArbetsorderSearchBasePanel } from "../../models/arbetsorder-search-base-panel";

@Component({
    selector: "rask-arbetsorder-search-arbetsmoment",
    templateUrl: "./arbetsorder-search-arbetsmoment.component.html",
    styleUrls: ["./arbetsorder-search-arbetsmoment.component.scss"],
    providers: [
        {
            provide: ArbetsorderSearchBasePanel,
            useExisting: forwardRef(() => ArbetsorderSearchArbetsmomentComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ArbetsorderSearchFilterComponent,
        CheckboxComponent,
        FormsModule,
        TreeSelectorComponent,
        SelectorComponent
    ]
})
export class ArbetsorderSearchArbetsmomentComponent extends ArbetsorderSearchBasePanel implements OnInit {
    allHandelssortiment: SelectionItem[] = [];
    allaMinaMaskinlag: IMaskinlag[] = [];
    @ViewChildren(ArbetsorderSearchBase) filterPanels: QueryList<ArbetsorderSearchBase>;
    minaMaskinlagItems: SelectionItem[] = [];
    restriktionsTyper = RestriktionsTyper.AllaWithKey.map(restriktion => ({
        id: restriktion.key,
        text: restriktion.value,
        value: restriktion.key,
        selected: false
    }));
    selectedHandelssortiment: IHandelssortimentFilter[];
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly arbetsorderSokService = inject(ArbetsorderSearchService);

    ansvarigFormatter = (result: ITypeAheadResult) => result ? {
        id: result.id,
        namn: result.namn
    } : undefined;

    ansvarigSelectedFormatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    ansvarigViewFormatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    applyFilter() {
        this.filterPanels.forEach(x => x.forceUpdateFilterChange());
        this.applyHSFilter();
    }

    applyHSFilter() {
        this.selectedHandelssortiment = this.searchFilter.handelssortiment;
        this.allHandelssortiment.forEach(ahsg => {
            ahsg.selected = (this.selectedHandelssortiment.findIndex(selectedHS => selectedHS.id === ahsg.id && selectedHS.typ === "HG") > -1);
            if (ahsg.selected) {
                ahsg.children.forEach(ahs => ahs.selected = true);
            } else {
                ahsg.children.forEach(ahs => {
                    ahs.selected = (this.selectedHandelssortiment.findIndex(selectedHS => selectedHS.id === ahs.id && selectedHS.typ === "HS") > -1);
                });
            }
            if (ahsg.children.length) {
                ahsg.selected = ahsg.children.every(hs => hs.selected);
                ahsg.partialSelected = (!ahsg.selected && ahsg.children.some(hs => hs.selected));
                ahsg.expanded = (ahsg.selected || ahsg.partialSelected);
            }
        });
    }

    forceUpdateFilterChange(): void {
        this.refreshFilter();
        this.applyFilter();
    }

    getModelFromArray(arr: boolean[], boolToHandle: boolean): boolean {
        return arr?.includes(boolToHandle);
    }

    hasCheckedValue(value: any) {
        return !value ? false : true;
    }

    isDirty(): boolean {
        return (
            (this.searchFilter.status.produktionsstatus?.destinerad?.length > 0) ||
            (this.searchFilter.status.produktionsstatus?.drivningspunkter?.length > 0) ||
            (this.searchFilter.status.produktionsstatus?.produkter?.length > 0) ||
            (this.searchFilter.status.produktionsstatus?.maskinlag?.length > 0) ||
            (this.searchFilter.handelssortiment?.length > 0) ||
            (this.searchFilter.maskinlag?.length > 0) ||
            (this.searchFilter.status.schemalagd?.length > 0) ||
            (this.searchFilter.status.skickadTillViol?.length > 0) ||
            (this.searchFilter.status.arbetsorderAvslutad === true) ||
            (this.searchFilter.status.extraordinarhandelse === true) ||
            (this.searchFilter.restriktioner?.length > 0) ||
            (this.searchFilter.restriktionStatus?.aktiv ?? false) ||
            (this.searchFilter.restriktionStatus?.inaktiv ?? false) ||
            (this.searchFilter.restriktionStatus?.na ?? false)
        );
    }

    ngOnInit() {
        this.setupMinamaskinlag();
        const handelssortimentslistafrandto = ((this.basdata as any).handelssortimentsgrupper as HandelssortimentsgruppDTO[]);
        this.allHandelssortiment = handelssortimentslistafrandto.map(hsg => ({
            text: hsg.namn,
            id: hsg.id,
            children: hsg.children.map(hs => ({
                text: hs.namn,
                id: hs.id
            } as SelectionItem))
        } as SelectionItem));

        this.applyHSFilter();
    }

    refreshFilter() {
        this.minaMaskinlagItems.forEach(maskinlag => {
            maskinlag.selected = (this.searchFilter.maskinlag.findIndex(ms => ms.id === maskinlag.id) > -1);
        });
        this.restriktionsTyper.forEach(restriktion => {
            restriktion.selected = (this.searchFilter.restriktioner.findIndex(r => r === restriktion.value) > -1);
        });
    }

    searchForAnsvarigAvverningsledareTypeAhead = (input: string) => this.arbetsorderService.getArbetsorderAnsvarigAvverkningsledareTypeAhead(input);

    // Alla dessa arrays är riktigt konstiga. De kan innehålla både true och false.
    setValue(arr: boolean[], boolToHandle: boolean, status: boolean) {
        arr.splice(0, arr.length);
        if (status) {
            arr.push(boolToHandle);
        }
        this.valtMaskinlagUnsetsEjTidsatta();
    }

    setupMinamaskinlag() {
        this.arbetsorderSokService.getMinaMaskinlag().subscribe(maskinlaglist => {
            this.allaMinaMaskinlag = maskinlaglist;
            this.minaMaskinlagItems = this.allaMinaMaskinlag.map(maskinlag => ({
                id: maskinlag.id,
                text: `${maskinlag.csrNummer} ${maskinlag.namn}`,
                value: maskinlag
            }));
            this.refreshFilter();
        });
    }

    updateMinamaskinlag() {
        this.searchFilter.maskinlag.splice(0, this.searchFilter.maskinlag.length);
        this.minaMaskinlagItems.forEach(maskinlag => {
            if (maskinlag.selected) {
                this.searchFilter.maskinlag.push(maskinlag.value);
            }
        });
        this.valtMaskinlagUnsetsEjTidsatta();
    }

    updateRestriktioner() {
        this.searchFilter.restriktioner.splice(0, this.searchFilter.restriktioner.length);
        this.restriktionsTyper.forEach(restriktion => {
            if (restriktion.selected) {
                this.searchFilter.restriktioner.push(restriktion.value);
            }
        });
    }

    updateSelectedHandelssortiment() {
        this.selectedHandelssortiment = [];
        this.allHandelssortiment.forEach(hsg => {
            if (hsg.selected) {
                this.selectedHandelssortiment.push({
                    typ: "HG",
                    id: hsg.id
                });
            }

            if (hsg.selected || hsg.partialSelected) {
                if (hsg.children) {
                    hsg.children.forEach(hs => {
                        if (hs.selected) {
                            this.selectedHandelssortiment.push({
                                typ: "HS",
                                id: hs.id
                            });
                        }
                    });
                }
            }
        });
        this.searchFilter.handelssortiment = this.selectedHandelssortiment;
    }

    private valtMaskinlagUnsetsEjTidsatta() {
        if ((this.searchFilter.status.produktionsstatus?.maskinlag?.length > 0
            && this.searchFilter.status.produktionsstatus?.maskinlag?.some(x => x.valueOf() === true))
            || this.searchFilter.maskinlag?.length > 0) {
            this.searchFilter.ejTidsatta = null;
        }
    }
}
